import { MachineMarker } from '@/flightpath-components/models/mqttPayload/MachineMarkerMessage';
import { MachineKey } from '@/flightpath-components/models/MachineKey';
import { GPSFixStatus } from '@/flightpath-components/models/enums/GPSFixStatus';
import { RosState } from '@/flightpath-components/models/enums/RosState';
import { Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { GPSLocation } from '@/flightpath-components/models/GPSLocation';
import { IDictionary } from '@/flightpath-components/models/IDictionary';
import { ConvertMachineStatus, ExxactMachineStatus, SteeringMode } from '@/models/ros-application/ExxactMachineStatus';
import { SortHelper } from '@/services/SortHelper';
import { round } from 'lodash';
import { MissionSpeedUserRequest } from '@/models/ui-application/MissionSpeedUserRequest';
import { WorkAreaStatus } from '@/models/ros-application/WorkAreaStatus';
import { DieselEngineInfo } from '@/models/ros-application/DieselEngineInfo';
import { EngineLampInfo } from '@/models/ros-application/EngineLampInfo';
import { HydrogenSystemInfo } from '@/models/ros-application/HydrogenSystemInfo';
import { InverterInfo } from '@/models/ros-application/InvertedInfo';
import { MissionProgress } from '@/models/ros-application/MissionProgress';

const SPEED_CONVERSION = 3.6;

function getFromCache<T>(key: string, cache?: IDictionary<string>): T | null {
  if (cache == null) return null;
  if (typeof cache[key] === 'undefined') {
    return null;
  }
  const payload = cache[key];
  const status = JSON.parse(payload) as T;
  return status;
}
@Module({ name: 'machine' })
export default class MachineStore extends VuexModule {
  machines: Array<MachineKey> = [];
  selectedMachine: MachineKey | null = null;
  selectedMachineMarker: MachineMarker | null = null;
  selectedMachineIndex: number | null = null;
  isRecordingPlannedPath = false;
  isRecordingPaused = false;
  recordedPathPoints: { loc: GPSLocation; timestamp: Date }[] = [];

  @Mutation
  setSelectedMachine(machine: MachineKey | null) {
    this.selectedMachine = machine;
  }
  @Mutation
  setSelectedMachineMarker(machine: MachineMarker | null) {
    this.selectedMachineMarker = machine;
  }
  @Mutation
  setSelectedMachineIndex(index: number | null) {
    this.selectedMachineIndex = index;
  }

  @Mutation
  setAvailableMachines(machines: Array<MachineKey>) {
    this.machines = machines.sort((a, b) => SortHelper.compareStrings(a.MachineName as string, b.MachineName as string));
  }

  @Mutation
  setRecordPlannedPath(record: boolean) {
    this.isRecordingPlannedPath = record;
    if (!record) {
      this.isRecordingPaused = false;
      this.recordedPathPoints = [];
    }
  }

  @Mutation
  setPausePathRecording(pause: boolean) {
    this.isRecordingPaused = pause;
  }

  @Mutation
  pushNewRecordedPathPoint(data: { loc: GPSLocation; timestamp: Date }) {
    // insert if the timestamp doesn't exist in the current points
    // and then order the points by their timestamp
    if (this.recordedPathPoints.findIndex((x) => x.timestamp == data.timestamp) == -1) {
      this.recordedPathPoints.push(data);

      this.recordedPathPoints.sort((a, b) => {
        return a.timestamp.getTime() - b.timestamp.getTime();
      });
    }
  }

  get applicationCache(): IDictionary<string> {
    //ros_application/mission_status
    if (this.selectedMachineMarker?.RosApplicationCache == null) return {};
    return this.selectedMachineMarker?.RosApplicationCache;
  }

  get missionProgress(): MissionProgress | null {
    return getFromCache('mission_progress', this.applicationCache);
  }
  get machineMissionStatus(): ExxactMachineStatus | null {
    return getFromCache('ros-application/machine_status', this.applicationCache);
  }
  get dieselEngineInfo(): DieselEngineInfo | null {
    return getFromCache('ros-application/diesel_engine_info', this.applicationCache);
  }
  get engineLampInfo(): EngineLampInfo | null {
    return getFromCache('ros-application/engine_lamp_info', this.applicationCache);
  }
  get workAreaStatus(): WorkAreaStatus | null {
    return getFromCache('ros-application/work_area_status', this.applicationCache);
  }

  get hydrogenSystemInfo(): HydrogenSystemInfo | null {
    return getFromCache('ros-application/hydrogen_system_info', this.applicationCache);
  }

  get inverterInfo(): InverterInfo | null {
    return getFromCache('ros-application/inverter_info', this.applicationCache);
  }

  get uiCache(): IDictionary<string> {
    if (this.selectedMachineMarker?.UIApplicationCache == null) return {};
    return this.selectedMachineMarker?.UIApplicationCache;
  }
  get speedStatus(): MissionSpeedUserRequest | null {
    return getFromCache('ui-application/mission_speed_user_request', this.uiCache);
  }

  get rtkState() {
    if (this.selectedMachineMarker == null) return false;
    return this.selectedMachineMarker.GPSFixStatus == 4 || this.selectedMachineMarker.GPSFixStatus == 0;
  }
  get gpsStatus() {
    if (this.selectedMachineMarker == null) return -1;
    return this.selectedMachineMarker.GPSFixStatus as GPSFixStatus;
  }
  get gpsStatusDisplay() {
    return GPSFixStatus[this.gpsStatus];
  }
  get rosStatus() {
    if (this.selectedMachineMarker == null) return 0;
    return this.selectedMachineMarker.RosState as RosState;
  }
  get rosStatusDisplay() {
    return RosState[this.rosStatus];
  }
  get machineLocation(): GPSLocation | null {
    if (this.selectedMachineMarker?.Location == null) return null;
    return this.selectedMachineMarker.Location.Location;
  }
  get machineLatitude(): number {
    if (this.selectedMachineMarker?.Location == null) return 0;
    return this.selectedMachineMarker.Location.Location.Latitude;
  }
  get machineLongitude(): number {
    if (this.selectedMachineMarker?.Location == null) return 0;
    return this.selectedMachineMarker.Location.Location.Longitude;
  }
  get machineBearing(): number {
    if (this.selectedMachineMarker?.Location == null) return 0;
    return Math.round(this.selectedMachineMarker.Location.Location.Bearing);
  }
  get machineSpeed(): number {
    if (this.selectedMachineMarker?.Location == null) return 0;
    return Math.round(this.selectedMachineMarker.Location.Location.Speed);
  }

  get currentMachineIndex(): number | null {
    return this.selectedMachineIndex;
  }

  get availableMachines(): Array<MachineKey> {
    return this.machines;
  }

  get machine(): MachineKey | null {
    return this.selectedMachine;
  }

  get isRecording(): boolean {
    return this.isRecordingPlannedPath;
  }

  get machineName() {
    return this.selectedMachine?.MachineName == null ? this.selectedMachine?.machineId : this.selectedMachine?.MachineName;
  }

  get vehicleSpeed() {
    if (this.machineMissionStatus == null) return 0;
    return round(this.machineMissionStatus.speed * SPEED_CONVERSION, 1);
  }

  get engineSpeed() {
    if (this.machineMissionStatus == null) return 0;
    return Math.round(this.machineMissionStatus.engine_speed / 10) * 10;
  }

  get fuelRate() {
    return this.dieselEngineInfo?.fuel_rate_litre_per_hour ?? 0;
  }

  get batteryPotential() {
    return this.dieselEngineInfo?.battery_potential ?? 0;
  }

  get oilTemp() {
    return this.dieselEngineInfo?.oil_temp ?? 0;
  }

  get coolantTemp() {
    return this.dieselEngineInfo?.coolant_temp ?? 0;
  }

  get remainingServiceHours() {
    return this.engineLampInfo?.remaining_service_hours ?? 0;
  }

  get engineWarningLamp() {
    return this.engineLampInfo?.engine_warning_lamp ?? 0;
  }
  get engineRedStopLamp() {
    return this.engineLampInfo?.engine_red_stop_lamp ?? 0;
  }
  get engineRunningLamp() {
    return this.engineLampInfo?.engine_running_lamp ?? 0;
  }
  get serviceDelayLamp() {
    return this.engineLampInfo?.service_delay_lamp ?? 0;
  }
  get engineDiagnosticLamp() {
    return this.engineLampInfo?.engine_diagnostic_lamp ?? 0;
  }
  get engineOvertempLamp() {
    return this.engineLampInfo?.engine_overtemp_lamp ?? 0;
  }
  get airFilterSwitchLamp() {
    return this.engineLampInfo?.air_filter_switch_lamp ?? 0;
  }
  get alternatorStatusLamp() {
    return this.engineLampInfo?.alternator_status_lamp ?? 0;
  }

  get tank1Temp() {
    return this.hydrogenSystemInfo?.tank1_temperature ?? 0;
  }
  get tank2Temp() {
    return this.hydrogenSystemInfo?.tank2_temperature ?? 0;
  }
  get tankLevelPercentage() {
    return this.hydrogenSystemInfo?.tank_level_percentage ?? 0;
  }
  get batteryChargePercentage() {
    return this.hydrogenSystemInfo?.battery_charge_percentage ?? 0;
  }
  get systemState() {
    return this.hydrogenSystemInfo?.system_state;
  }
  get storageState() {
    return this.hydrogenSystemInfo?.storage_state;
  }

  get inverterSpeed() {
    return this.inverterInfo?.inverter_speed_rmp ?? 0;
  }
  get acCurrent() {
    return this.inverterInfo?.ac_current ?? 0;
  }
  get inverterErrorState() {
    return this.inverterInfo?.error_state ?? false;
  }
  get inverterErrorCode() {
    return this.inverterInfo?.error_code;
  }

  get fuelPercentage() {
    if (this.machineMissionStatus == null) return 0;
    return this.machineMissionStatus.fuel_percentage * 100;
  }
  get steeringStatus(): SteeringMode {
    if (this.machineMissionStatus == null) return { mode: 0 };
    return this.machineMissionStatus.steering_mode;
  }

  get isPathRecordingPaused() {
    return this.isRecordingPaused;
  }

  get recordedPath() {
    return this.recordedPathPoints.map((x) => x.loc);
  }
}
